import axios from 'axios';
import { ORDER_GENIE_API_BASE_URL } from '../config';
import { getAuthHeadersFromStorage } from '../utils/authHeaders';
import { attachAuthInterceptor } from '../utils/attachAuthInterceptor';

const client = axios.create({
  baseURL: ORDER_GENIE_API_BASE_URL,
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  // getAuthHeadersFromStorage() already prioritizes OAuth over API key and
  // returns the right single credential — don't also attach a (possibly
  // stale) X-API-Key on top, or an OAuth request can carry a leftover key
  // the backend rejects even though the Bearer token is valid.
  const authHeaders = getAuthHeadersFromStorage();
  if (Object.keys(authHeaders).length) {
    config.headers = { ...authHeaders, ...config.headers };
  }
  return config;
});
attachAuthInterceptor(client);

const READINESS_READ_TIMEOUT_MS = 15000;
const READINESS_RUN_TIMEOUT_MS = 300000;
// Status polls run while a heavy assessment shares the backend event loop, so a
// single read can be slow under load. Give it a wider budget than other reads
// and let the caller tolerate transient breaches.
const READINESS_STATUS_TIMEOUT_MS = 30000;

function formatApiDetail(detail) {
  if (detail == null || detail === '') return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          if (item.msg) return String(item.msg);
          if (item.message) return String(item.message);
        }
        try {
          return JSON.stringify(item);
        } catch (_err) {
          return 'Invalid request';
        }
      })
      .filter(Boolean)
      .join(', ');
  }
  if (typeof detail === 'object') {
    if (detail.message) return String(detail.message);
    if (detail.msg) return String(detail.msg);
    try {
      return JSON.stringify(detail);
    } catch (_err) {
      return 'Request failed';
    }
  }
  return String(detail);
}

const extractError = (error) => {
  const payload = error?.response?.data;
  if (payload?.error === 'order_genie_service_unavailable') {
    return payload?.message || 'OrderX service is not reachable. Start it on port 8001 and retry.';
  }
  if (payload?.detail != null) {
    return formatApiDetail(payload.detail) || 'Request failed';
  }
  if (payload?.message) {
    return String(payload.message);
  }
  if (error?.code === 'ECONNABORTED') {
    return 'Request timed out. Check that the backend and OrderX services are running.';
  }
  return error?.message || 'Request failed';
};

export const createOrderGenieRequest = async ({
  requestName,
  userId,
  customerIdAsInErp,
  selectedAgents,
  aiEngine,
  selectedSheet,
  notifyByEmail = true,
  notifyBySms = true,
  notificationEmail,
  notificationPhone,
  notificationAdditionalEmails,
  notificationAdditionalPhones,
  files,
}) => {
  try {
    const formData = new FormData();
    formData.append('request_name', requestName);
    formData.append('user_id', userId);
    formData.append('notify_by_email', notifyByEmail ? 'true' : 'false');
    formData.append('notify_by_sms', notifyBySms ? 'true' : 'false');
    formData.append('notification_email', String(notificationEmail || '').trim());
    formData.append('notification_phone', String(notificationPhone || '').trim());
    formData.append('notification_additional_emails', String(notificationAdditionalEmails || '').trim());
    formData.append('notification_additional_phones', String(notificationAdditionalPhones || '').trim());
    if (String(customerIdAsInErp || '').trim()) {
      formData.append('customer_id_erp', String(customerIdAsInErp || '').trim());
    }
    if (selectedAgents && selectedAgents.length) {
      formData.append('selected_agents', JSON.stringify(selectedAgents));
    }
    if (aiEngine) {
      formData.append('ai_engine', aiEngine);
    }
    if (String(selectedSheet || '').trim()) {
      formData.append('selected_sheet', String(selectedSheet || '').trim());
    }
    files.forEach((file) => {
      formData.append('files', file);
    });
    const { data } = await client.post('/api/v1/orderx/requests', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listOrderGenieRequests = async (userId, options = {}) => {
  try {
    const {
      page,
      pageSize,
      dateFrom,
      dateTo,
      lite = true,
      minimal = false,
      requestName,
      statuses,
      customer,
      defaultExcludeTerminal,
    } = options;
    const params = {
      lite,
      minimal,
      page,
      page_size: pageSize,
      date_from: dateFrom,
      date_to: dateTo,
    };
    if (userId) {
      params.user_id = userId;
    }
    if (requestName) {
      params.request_name = requestName;
    }
    if (Array.isArray(statuses) && statuses.length) {
      params.statuses = statuses;
    }
    if (customer) {
      params.customer = customer;
    }
    if (defaultExcludeTerminal) {
      params.default_exclude_terminal = defaultExcludeTerminal;
    }
    const { data } = await client.get('/api/v1/orderx/requests', {
      params,
      // Axios's default array serialization emits `statuses[]=a&statuses[]=b`, but
      // FastAPI's `statuses: Optional[List[str]] = Query(None)` only binds the plain
      // repeated form `statuses=a&statuses=b`. Without this, the statuses param is
      // silently dropped server-side no matter what the caller sends.
      paramsSerializer: { indexes: null },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getOrderGenieRequest = async (requestId, userId) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/requests/${requestId}`, { params: { user_id: userId } });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// Story 3.4 (FR-5): the Reviewer-facing "basis" for a Learned Match candidate
// -- when the correction happened and what was corrected. Ordinary
// account-scoped auth only, no admin role required.
export const getOrderGenieLearningRecordBasis = async (recordId) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/learning-records/${encodeURIComponent(recordId)}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// Same payload as getOrderGenieRequest, looked up by the request's opaque
// `request_code` (its public_share_token) instead of the numeric id -- used to
// resolve the `?openRequest=<code>` deep link so the URL never carries the raw
// BIGSERIAL id (one sequence shared across every account on the platform).
export const getOrderGenieRequestByCode = async (code, userId) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/requests/by-code/${encodeURIComponent(code)}`, {
      params: { user_id: userId },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getOrderGenieRunDetails = async (requestId, userId, limit = 200) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/requests/${requestId}/run-details`, {
      params: { user_id: userId, limit },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const parseOrderGenieRequest = async (requestId, payload) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/requests/${requestId}/parse`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateOrderGenieItems = async (requestId, payload) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/requests/${requestId}/items`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const matchOrderGenieRequest = async (requestId, payload) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/requests/${requestId}/match`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const matchOrderGenieRequestItem = async (requestId, payload) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/requests/${requestId}/match-item`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const processOrderGenieRequestAsync = async (requestId, payload) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/requests/${requestId}/process-async`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getOrderGenieSetupSecrets = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/settings/setup-secrets');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const saveOrderGenieSetupSecrets = async (secrets) => {
  try {
    const { data } = await client.post('/api/v1/orderx/settings/setup-secrets', { secrets });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getOrderGenieProcessStatus = async (requestId, userId) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/requests/${requestId}/process-status`, {
      params: { user_id: userId },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const probeOrderGenieRequest = async (requestId, userId) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/requests/${requestId}/probe`, {
      params: { user_id: userId },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getOrderGenieProcessStatusBulk = async (payload) => {
  try {
    const { data } = await client.post('/api/v1/orderx/requests/process-status-bulk', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listOrderGenieQueue = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/orderx/queue', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getOrderGenieQueueMetrics = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/queue/metrics');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listOrderGenieUsageEvents = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/orderx/usage-events', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateOrderGenieRequestPriority = async (requestId, payload) => {
  try {
    const { data } = await client.patch(`/api/v1/orderx/requests/${requestId}/priority`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const recordOrderGenieDuplicateResolution = async (requestId, payload) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/requests/${requestId}/duplicate-resolutions`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const replayOrderGenieQueueRequest = async (requestId, payload) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/requests/${requestId}/queue/replay`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const stopOrderGenieProcess = async (requestId, payload) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/requests/${requestId}/process-stop`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listOrderGenieRequestUsers = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/request-users');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const searchOrderGenieErpCustomers = async (query) => {
  try {
    const q = String(query || '').trim();
    const { data } = await client.get('/api/v1/orderx/erp-customers/search', {
      params: { q, customer_name: q },
    });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      connector: data?.connector || 'p21',
    };
  } catch (error) {
    throw new Error(extractError(error));
  }
};

/**
 * Hybrid customer typeahead: runs the live ERP search and the
 * customer_directory OpenSearch (lexical + kNN) search in parallel on the
 * backend and returns one merged, source-tagged list. Each item carries
 * `source`: 'verified' (ERP + index agree) | 'erp' (ERP only, not indexed
 * yet) | 'opensearch' (indexed only, not confirmed against ERP this
 * request). `lanes` reports per-source status so the UI can explain a
 * missing/slow lane instead of just showing fewer results.
 */
export const lookupOrderGenieCustomersHybrid = async (query, { pageSize = 8 } = {}) => {
  try {
    const q = String(query || '').trim();
    if (q.length < 2) {
      return { items: [], lanes: { erp: 'skipped', opensearch: 'skipped' } };
    }
    const { data } = await client.get('/api/v1/orderx/customers/lookup', {
      params: { q, page_size: pageSize },
    });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      lanes: data?.lanes || {},
    };
  } catch (error) {
    throw new Error(extractError(error));
  }
};

/** Eclipse (and other no-search connectors): exact entityId lookup. */
export const lookupOrderGenieErpCustomerByEntityId = async (entityId) => {
  try {
    const code = String(entityId || '').trim();
    if (!code) {
      return null;
    }
    const { data } = await client.get('/api/v1/orderx/erp-customers/lookup', {
      params: { entity_id: code },
    });
    return data || null;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const searchOrderGenieErpShipToAddresses = async (customerId, query = '') => {
  try {
    const cid = String(customerId || '').trim();
    if (!cid) {
      return { items: [] };
    }
    const { data } = await client.get(
      `/api/v1/orderx/erp-customers/${encodeURIComponent(cid)}/ship-to`
    );
    const items = Array.isArray(data?.items) ? data.items : [];
    const q = String(query || '').trim().toLowerCase();
    if (!q) {
      return { items };
    }
    return {
      items: items.filter(
        (s) =>
          String(s?.shipToId || '').toLowerCase().includes(q) ||
          String(s?.address || '').toLowerCase().includes(q)
      ),
    };
  } catch (error) {
    throw new Error(extractError(error));
  }
};

/**
 * Hybrid ship-to lookup for a known customer: runs the ERP ship-to list and
 * the customer_directory OpenSearch ship-to search (scoped to this
 * customer) in parallel on the backend. Same `source`/`lanes` contract as
 * `lookupOrderGenieCustomersHybrid`.
 */
export const lookupOrderGenieShipToHybrid = async (customerId, query = '') => {
  try {
    const cid = String(customerId || '').trim();
    if (!cid) {
      return { items: [], lanes: {} };
    }
    const { data } = await client.get('/api/v1/orderx/shipto/lookup', {
      params: { customer_id: cid, q: String(query || '').trim() },
    });
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      lanes: data?.lanes || {},
    };
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getOrderGenieErpCustomerById = async (customerId) => {
  try {
    const cid = String(customerId || '').trim();
    if (!cid) {
      return null;
    }
    const { data } = await client.get(
      `/api/v1/orderx/erp-customers/${encodeURIComponent(cid)}`
    );
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listOrderGenieErpContacts = async (customerId) => {
  try {
    const cid = String(customerId || '').trim();
    if (!cid) {
      return { items: [] };
    }
    const { data } = await client.get(
      `/api/v1/orderx/erp-customers/${encodeURIComponent(cid)}/contacts`
    );
    return {
      items: Array.isArray(data?.items) ? data.items : [],
    };
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const submitOrderGenieErpOrder = async (requestId, payload) => {
  try {
    const { data } = await client.post(
      `/api/v1/orderx/requests/${requestId}/erp-submit`,
      payload || {}
    );
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// Real bulk ERP price lookup (backed by a dummy deterministic price on the backend
// for now — see order_genie_service get_dummy_prices_bulk). Pass every product code
// you need priced in one call; the backend mirrors ERPs that accept an array of
// part numbers and return prices in a single round trip.
export const getOrderGenieErpPricesBulk = async (productCodes, options = {}) => {
  try {
    const customerIdErp = String(options.customer_id_erp || options.customerIdErp || '').trim();
    const { data } = await client.post('/api/v1/orderx/prices/bulk', {
      product_codes: productCodes,
      ...(customerIdErp ? { customer_id_erp: customerIdErp } : {}),
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateOrderGenieCart = async (requestId, payload) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/requests/${requestId}/cart`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateOrderGenieSummary = async (requestId, payload) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/requests/${requestId}/summary`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listOrderGenieQuotes = async (userId, options = {}) => {
  try {
    const { page, pageSize, dateFrom, dateTo, lite = true } = options;
    const params = {
      lite,
      page,
      page_size: pageSize,
      date_from: dateFrom,
      date_to: dateTo,
    };
    if (userId) {
      params.user_id = userId;
    }
    const { data } = await client.get('/api/v1/orderx/quotes', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const previewOrderGenieFile = async (file, options = {}) => {
  try {
    const selectedSheet = String(options?.selectedSheet || '').trim();
    const formData = new FormData();
    formData.append('file', file);
    if (selectedSheet) {
      formData.append('selected_sheet', selectedSheet);
    }
    if (options?.rowOffset != null) {
      formData.append('row_offset', String(options.rowOffset));
    }
    if (options?.rowLimit != null) {
      formData.append('row_limit', String(options.rowLimit));
    }
    if (options?.maxColumns != null) {
      formData.append('max_columns', String(options.maxColumns));
    }
    const { data } = await client.post('/api/v1/orderx/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const previewOrderGenieStoredFile = async (payload) => {
  try {
    const { data } = await client.post('/api/v1/orderx/preview-by-path', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// The "Fallback Product Image Base URL" lives in the global AHRI Match Lookup config
// (backend/api/ahri.py), not in OrderX's own settings — reused as-is so both surfaces
// stay in sync. Backend caches this per tenant (account) for 30s, so calling this from
// multiple pages doesn't re-run the full AHRI config load each time.
// Failure is non-fatal: callers fall back to showing no image prefix.
export const getOrderGenieProductImageBaseUrl = async () => {
  try {
    const { data } = await client.get('/api/v1/ahri/product-image-base-url');
    const url = data?.base_url;
    return typeof url === 'string' ? url.trim() : '';
  } catch (error) {
    return '';
  }
};

export const getOrderGenieSettings = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/settings');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getOrderGenieNotificationFlags = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/settings/notification-flags');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const testOrderGenieNotificationEmail = async (payload) => {
  try {
    const { data } = await client.post('/api/v1/orderx/settings/test-notification/email', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const testOrderGenieNotificationSms = async (payload) => {
  try {
    const { data } = await client.post('/api/v1/orderx/settings/test-notification/sms', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateOrderGenieSettings = async (payload) => {
  try {
    const { data } = await client.put('/api/v1/orderx/settings', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getOrderGenieProductMatchEngine = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/settings/product-match-engine');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getOrderGenieSystemDefaults = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/settings/system-defaults');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const searchOrderGenieProducts = async (payload) => {
  try {
    const { data } = await client.post('/api/v1/orderx/search/products', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const searchOrderGenieCustomers = async (payload) => {
  try {
    const { data } = await client.post('/api/v1/orderx/search/customers', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const searchOrderGenieShipToAddresses = async (payload) => {
  try {
    const { data } = await client.post('/api/v1/orderx/search/shipto', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateOrderGenieSystemDefaults = async (payload) => {
  try {
    const { data } = await client.put('/api/v1/orderx/settings/system-defaults', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const copyOrderGenieSystemDefaultsFromClient = async (sourceAccountId) => {
  try {
    const { data } = await client.post('/api/v1/orderx/settings/system-defaults/copy-from-client', {
      source_account_id: sourceAccountId,
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listOrderGenieSettingsClients = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/settings/clients');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listOrderGenieAgents = async (scope = 'client', includeSystemFallback = false) => {
  try {
    const { data } = await client.get('/api/v1/orderx/agents', {
      params: { scope, include_system_fallback: includeSystemFallback },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const createOrderGenieAgent = async (payload, scope = 'client') => {
  try {
    const { data } = await client.post('/api/v1/orderx/agents', payload, { params: { scope } });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateOrderGenieAgent = async (agentId, payload, scope = 'client') => {
  try {
    const { data } = await client.put(`/api/v1/orderx/agents/${agentId}`, payload, { params: { scope } });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteOrderGenieAgent = async (agentId, scope = 'client') => {
  try {
    const { data } = await client.delete(`/api/v1/orderx/agents/${agentId}`, { params: { scope } });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const copyOrderGenieSystemAgentsFromClient = async (sourceAccountId) => {
  try {
    const { data } = await client.post('/api/v1/orderx/agents/system/copy-from-client', {
      source_account_id: sourceAccountId,
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteOrderGenieRequest = async (requestId, userId) => {
  try {
    const { data } = await client.delete(`/api/v1/orderx/requests/${requestId}`, {
      params: { user_id: userId },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getReadinessLatest = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/readiness', {
      timeout: READINESS_READ_TIMEOUT_MS,
    });
    return data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw new Error(extractError(error));
  }
};

export const getReadinessRunStatus = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/readiness/run/status', {
      timeout: READINESS_STATUS_TIMEOUT_MS,
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const runReadinessAssessment = async (payload = {}) => {
  try {
    const response = await client.post('/api/v1/orderx/readiness/run', payload, {
      timeout: payload?.async_run ? READINESS_READ_TIMEOUT_MS : READINESS_RUN_TIMEOUT_MS,
    });
    return {
      ...(response.data || {}),
      httpStatus: response.status,
    };
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listReadinessSnapshots = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/orderx/readiness/snapshots', {
      params,
      timeout: READINESS_READ_TIMEOUT_MS,
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getReadinessSnapshot = async (snapshotId) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/readiness/snapshots/${snapshotId}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const exportReadinessSnapshot = async (snapshotId) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/readiness/snapshots/${snapshotId}/export`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const markReadinessReady = async (payload = {}) => {
  try {
    const { data } = await client.post('/api/v1/orderx/readiness/mark-ready', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listReadinessSymptoms = async () => {
  try {
    const { data } = await client.get('/api/v1/orderx/readiness/symptoms');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const postReadinessHeal = async (actionId, payload = {}) => {
  try {
    const { data } = await client.post(`/api/v1/orderx/readiness/heal/${actionId}`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getReadinessSmokeStatus = async (smokeRequestId, params = {}) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/readiness/smoke/${smokeRequestId}`, { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const getReadinessDiagnoseRequest = async (requestId, params = {}) => {
  try {
    const { data } = await client.get(`/api/v1/orderx/readiness/diagnose/request/${requestId}`, { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};
